import React, { useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import cx from "classnames";
import { DropTarget } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import { UPLOAD_SOURCES } from "@cld/upload-widget";
import t from "common/i18n";
import { isMobile } from "utils/domUtils";
import TabPanel from "tabs/hocs/TabPanel";
import { selectDictionaryTextVersion, selectClientAllowedFormatsOptions } from "root/selectors";
import TabFooter from "root/components/TabFooter";
import SvgIcon from "root/components/SvgIcon";
import { getComponentConsumer } from "root/components/UploadContainer/uploaderContext";
import Html5DragDropContext  from "./components/Html5DragDropContext";
import UploadButton from "./components/UploadButton";
import SVG_ICONS from "~/assets/icons/icons";

import "./LocalTab.scss";

const TabCenterContent = ({ isDragHover }) => {
	return <>
		<div className="icon-upload mb4 tc dn db-ns">
			<SvgIcon size={100} path={SVG_ICONS.UPLOAD}
			         colorName={isDragHover ? "action" : "window-border"}/>
		</div>

		<div className="mb4 tc db dn-ns">
			<SvgIcon size={125} path={SVG_ICONS.FILES_DRAG}
			         colorName="window-border"/>
		</div>

		<strong
			className="c-window-border title single-text">{t("local.dd_title_single")}</strong>
		<strong
			className="c-window-border title multi-text">{t("local.dd_title_multi")}</strong>
		<strong
			className="c-window-border mb4 title add single-text c-action">{t("local.drop_title_single")}</strong>
		<strong
			className="c-window-border mb4 title add multi-text c-action">{t("local.drop_title_multiple")}</strong>

		<span className="or">{t("or")}</span>
	</>;
};

const LocalTab = ({ isDragHover, connectDropTarget, uploader }) => {
	//re-render local tab on text changes
	useSelector(selectDictionaryTextVersion);
	const uploadAllowedFormat = useSelector(selectClientAllowedFormatsOptions);

	const buttonContainerRef = useRef(null),
		ddAreaRef = useRef(null);

	useEffect(() => {
		//render the upload field only once on mount
		uploader.renderUploadField(buttonContainerRef.current, ddAreaRef.current);
	},  []); // eslint-disable-line react-hooks/exhaustive-deps

	return connectDropTarget(<div className="panel-inner-container">
		<div
			data-test="drag-area"
			className={cx(
				"bg-window",
				"drag_area content-wrap bg-source-bg bg-source-bg-c",
				"browse-box", {
					"drag-active": isDragHover,
				},
			)}
			ref={ddAreaRef}>
			<div className="drag_content">
				<div className={cx("holder bg-source-bg bg-source-bg-c", {
					"bc-window-border": !isDragHover,
					"bc-action": isDragHover,
				})}>
					<div className="box centered">
						{!isMobile() && <TabCenterContent isDragHover={isDragHover}/>}
						<UploadButton
							allowedFormat={uploadAllowedFormat}
							ref={buttonContainerRef}
						/>
					</div>
				</div>
			</div>
		</div>
		<TabFooter/>
	</div>);
};

const dndSpec = {
	drop(props, monitor) {
		const dndItem = monitor.getItem();
		const files = dndItem && dndItem.urls;

		if (files) {
			props.addToUploadQueue({ files });
		}

		return {
			dndFinished: true,
		};
	},
};

const dndCollect = (connect, monitor) => ({
	connectDropTarget: connect.dropTarget(),
	isDragHover: monitor.isOver(),
});

const LocalTabWithDnD = Html5DragDropContext(DropTarget(
	[NativeTypes.FILE, NativeTypes.URL],
	dndSpec,
	dndCollect,
)(getComponentConsumer(LocalTab)));

export default TabPanel({
	id: UPLOAD_SOURCES.LOCAL,
	root: true,
})(LocalTabWithDnD);
